
import AppToolbar from '@/_components/bar/appToolbar/appToolbar.vue'
import VbitStatusBanner from '@/components/banner/vbitStatus.vue'
import { PROJECT_ACCESS_QUERY, PROJECT_QUERY } from '../gql/queries'
import UserRuleDialog from '../userRuleDialog.vue'
import GroupDialog from '../groupDialog.vue'
import NewCardDialog from '../newCardDialog.vue'
import NewCompanyDialog from '../newCompanyDialog.vue'
import { openDialog } from '@/_components/dialog/dialog'
import {
    CREATE_ACCESS_GROUP_CARD_ASSOCIATION,
    CREATE_ACCESS_GROUP_COMPANY_ASSOCIATION,
    DELETE_ACCESS_GROUP_CARD_ASSOCIATION,
    DELETE_ACCESS_GROUP_COMPANY_ASSOCIATION,
} from '../gql/mutations'
import { accessClient } from '@/apolloClient'
import { cloneDeep, debounce } from 'lodash'

export default {
    name: 'ProjectAccess',
    components: {
        AppToolbar,
        VbitStatusBanner,
    },
    data() {
        return {
            filter: {
                cardId: '',
                name: '',
                company: '',
            },
            project: null as any,
            allReadersForProject: [],
            allRulesForProject: [] as any,
            allGroupsForProject: [] as any,
            allGroupReaderRuleAssociationsForProject: [],
            allAccessGroupCompanyAssociationsForProject: [] as any,
            allAccessGroupCardAssociationsForProject: [] as any,
            isFetching: true,
            currentViewMode: 'cards',
            cards: [] as any[],
            cardsMap: {} as Record<string, any>,
        }
    },
    computed: {
        cardNotFound() {
            return !this.cards.some((card) => card._isVisible)
        },
        groupCardByGroupId() {
            return Object.groupBy(this.allAccessGroupCardAssociationsForProject, (accessGroup) => {
                return accessGroup.groupId
            })
        },
        groupReaderRuleByGroupId() {
            return Object.groupBy(this.allGroupReaderRuleAssociationsForProject, (group) => {
                return group.groupId
            })
        },
        allVisibleRulesForProject() {
            return this.allRulesForProject.filter((rule) => rule.type !== 'ADMIN')
        },
        allVisibleGroupsForProject() {
            return this.allGroupsForProject.filter((group) => group.type !== 'ADMIN')
        },
        cardIdWithoutSpaces() {
            return this.filter.cardId?.replaceAll(/\s/g, '') || ''
        },
        lowerCaseNameSearchTerm() {
            return this.filter.name?.toLowerCase() || ''
        },
        lowerCaseCompanySearchTerm() {
            return this.filter.company?.toLowerCase() || ''
        },
        withPsi() {
            return Boolean(this.project?.settings.denyWithoutPsi)
        },
    },
    methods: {
        isCardVisible(card: any) {
            return (
                (!this.cardIdWithoutSpaces || card._cardId.includes(this.cardIdWithoutSpaces)) &&
                (!this.lowerCaseNameSearchTerm ||
                    card._userName.toLowerCase().includes(this.lowerCaseNameSearchTerm)) &&
                (!this.lowerCaseCompanySearchTerm ||
                    card._companyName.toLowerCase().includes(this.lowerCaseCompanySearchTerm))
            )
        },
        filterCards: debounce(function () {
            this.cards.forEach((card) => {
                card._isVisible = this.isCardVisible(card)
            })
        }, 300),
        async updateAccessGroupCardAssociation(card: any, group: any) {
            if (!card._canBeAssignedToGroup) {
                return
            }

            if (!card._groups[group.id]) {
                this.$set(card._groups, group.id, { checked: false, isLoading: false })
            }

            if (card._groups[group.id].isLoading) {
                return
            }

            const isSet = !card._groups[group.id].checked

            try {
                let errors = []
                card._groups[group.id].isLoading = true
                await new Promise((resolve) => setTimeout(resolve, 100))
                const groupCardVariables = {
                    groupId: group.id,
                    cardId: card.cardId,
                    withPsi: this.withPsi,
                }
                const groupCompanyVariables = {
                    groupId: group.id,
                    companyId: card.company?.id || null,
                }

                if (isSet) {
                    if (card.bklrInternalCompanyId) {
                        const result = await accessClient.mutate({
                            mutation: CREATE_ACCESS_GROUP_COMPANY_ASSOCIATION,
                            variables: groupCompanyVariables,
                        })
                        errors = result.data?.createAccessGroupCompanyAssociation?.errors
                        if (result.data?.createAccessGroupCompanyAssociation?.association) {
                            this.allAccessGroupCardAssociationsForProject.push({
                                ...result.data.createAccessGroupCompanyAssociation.association,
                            })
                        }
                    } else {
                        const result = await accessClient.mutate({
                            mutation: CREATE_ACCESS_GROUP_CARD_ASSOCIATION,
                            variables: groupCardVariables,
                        })
                        errors = result.data?.createAccessGroupCardAssociation?.errors
                        if (result.data?.createAccessGroupCardAssociation?.association) {
                            this.allAccessGroupCardAssociationsForProject.push({
                                ...result.data.createAccessGroupCardAssociation.association,
                            })
                        }
                    }
                } else {
                    if (card.bklrInternalCompanyId) {
                        const result = await accessClient.mutate({
                            mutation: DELETE_ACCESS_GROUP_COMPANY_ASSOCIATION,
                            variables: groupCompanyVariables,
                        })
                        errors = result.data?.deleteAccessGroupCompanyAssociation?.errors
                        this.allAccessGroupCompanyAssociationsForProject =
                            this.allAccessGroupCompanyAssociationsForProject.filter(
                                (groupCard) => groupCard.groupId !== group.id
                            )
                    } else {
                        const result = await accessClient.mutate({
                            mutation: DELETE_ACCESS_GROUP_CARD_ASSOCIATION,
                            variables: groupCardVariables,
                        })
                        errors = result.data?.deleteAccessGroupCardAssociation?.errors
                        this.allAccessGroupCardAssociationsForProject =
                            this.allAccessGroupCardAssociationsForProject.filter(
                                (groupCard) =>
                                    groupCard.groupId !== group.id || groupCard.card.id !== card.id
                            )
                    }
                }
                if (Array.isArray(errors) && errors.length) {
                    this.showErrors(errors.map((error: any) => error.message).join('\n'))
                } else {
                    card._groups[group.id].checked = isSet
                }
            } finally {
                card._groups[group.id].isLoading = false
            }
        },
        showErrors(message: string) {
            this.$store.commit('setSuccessSnackBar', { subtitle: message, timeout: 5000 })
        },
        async openUserRule(card: any) {
            await openDialog(UserRuleDialog, {
                card,
                rules: this.allVisibleRulesForProject,
                groups: this.allVisibleGroupsForProject,
                groupCards: this.allAccessGroupCardAssociationsForProject.filter(
                    (groupCard) =>
                        groupCard.card.id === card.id || groupCard.groupId === card.groupId
                ),
                groupCompanies: this.allAccessGroupCompanyAssociationsForProject.filter(
                    (groupCompany) =>
                        groupCompany.bklrInternalCompanyId === card.bklrInternalCompanyId
                ),
                rulesByGroup: this.groupReaderRuleByGroupId,
            })
        },
        async openGroup(group: any) {
            const isAdminGroup = group.type === 'ADMIN'
            const dialog = await openDialog(GroupDialog, {
                group,
                rules: isAdminGroup
                    ? this.allRulesForProject.filter((rule) => rule.type === 'ADMIN')
                    : this.allVisibleRulesForProject,
                readerRules: this.groupReaderRuleByGroupId[group.id],
                readers: this.allReadersForProject,
                disabled: isAdminGroup,
            })
            if (dialog?.updatedGroup) {
                const updatedGroup = cloneDeep(dialog.updatedGroup)
                this.allGroupsForProject = this.allGroupsForProject.map((groupItem) => {
                    if (groupItem.id === updatedGroup.id) {
                        return updatedGroup
                    }
                    return groupItem
                })
            }
        },
        highlightRow(id: string) {
            setTimeout(() => {
                const cardRow = document.querySelector(`tr[data-card-id$="${id}"]`)
                if (!cardRow) return

                cardRow.scrollIntoView({ block: 'center' })
                cardRow.dataset.highlighted = 'true'
                setTimeout(() => {
                    cardRow.removeAttribute('data-highlighted')
                }, 3000)
            }, 500)
        },
        async openNewCompanyDialog() {
            const dialog = await openDialog(NewCompanyDialog, {
                groups: this.allVisibleGroupsForProject,
            })
            if (dialog?.companyAdded && Array.isArray(dialog.companyAdded)) {
                dialog.companyAdded.forEach((groupCompany: any) => {
                    this.allAccessGroupCompanyAssociationsForProject.push(groupCompany)
                })
                this.sortAndSetCards()
                this.highlightRow(dialog.companyAdded[0].bklrInternalCompanyId)
            }
        },
        async openNewCardDialog(groups: string[] = [], cardId = '') {
            const dialog = await openDialog(NewCardDialog, {
                groups: this.allVisibleGroupsForProject,
                cardId,
                selectedGroups: groups,
                withPsi: this.withPsi,
            })
            if (dialog?.cardAdded && Array.isArray(dialog.cardAdded)) {
                dialog.cardAdded.forEach((groupCard: any) => {
                    this.allAccessGroupCardAssociationsForProject.push(groupCard)
                })
                this.sortAndSetCards()
                this.highlightRow(dialog.cardAdded[0].card.cardId)
            }
        },
        getCardId(card: any) {
            return card.type === 'VIRTUAL'
                ? this.formatPhoneNumber(card.cardId)
                : card.cardId.padStart(4, '0')
        },
        setGroupCompanyById(groupCompany: any) {
            const id = groupCompany.bklrInternalCompanyId
            if (!this.cardsMap[id]) {
                const _cardId = id
                const _userName = this.$t(`fe_access['text.all_cards']`)
                const _companyName =
                    groupCompany.company?.name ||
                    groupCompany.bklrCompanyName ||
                    this.$t(`fe_access['text.all_companies']`)
                this.cardsMap[id] = {
                    ...groupCompany,
                    _id: '-',
                    _isVisitor: false,
                    _cardId,
                    _userName,
                    _companyName,
                    _isVisible: this.isCardVisible({ _cardId, _userName, _companyName }),
                    _isCardExpired: false,
                    _canBeAssignedToGroup: true,
                    _groups: {},
                }
                this.cards.push(this.cardsMap[id])
            }
            this.$set(this.cardsMap[id]._groups, groupCompany.groupId, {
                checked: true,
                isLoading: false,
            })
        },
        setGroupCardById(groupCard: any) {
            const id = groupCard.card.cardId
            const _id = this.getCardId(groupCard.card)
            const today = new Date()
            if (!this.cardsMap[groupCard.card.cardId]) {
                const _cardId = id.padStart(4, '0')
                const _userName =
                    groupCard.card.bklrUser.name ||
                    this.$t(`fe_access['text.visitor_card_number']`, { number: _id })
                const _companyName =
                    groupCard.card.company?.name || this.$t(`fe_access['text.visitor_card']`)
                this.cardsMap[id] = {
                    ...groupCard.card,
                    _id,
                    _isVisitor: groupCard.card.type === 'VISITOR',
                    _cardId,
                    _userName,
                    _companyName,
                    _isVisible: this.isCardVisible({ _cardId, _userName, _companyName }),
                    _isCardExpired: groupCard.card.expiresAt
                        ? new Date(groupCard.card.expiresAt) < today
                        : false,
                    _canBeAssignedToGroup: groupCard.card.status
                        ? groupCard.card.status === 'VALID' ||
                          !this.project.settings.denyInvalidCards
                        : true,
                    _groups: {},
                }
                this.cards.push(this.cardsMap[id])
            }
            this.$set(this.cardsMap[id]._groups, groupCard.groupId, {
                checked: true,
                isLoading: false,
            })
        },
        sortAndSetCards() {
            this.allAccessGroupCompanyAssociationsForProject.sort((a, b) => {
                if (a.company?.name && b.company?.name) {
                    return a.company.name.localeCompare(b.company.name)
                }
                return 1
            })

            this.allAccessGroupCardAssociationsForProject.sort((a, b) => {
                if (a.card.bklrUser.name && b.card.bklrUser.name) {
                    return a.card.bklrUser.name.localeCompare(b.card.bklrUser.name)
                } else if (a.card.bklrUser.name && !b.card.bklrUser.name) {
                    return -1
                } else if (!a.card.bklrUser.name && b.card.bklrUser.name) {
                    return 1
                }
                return a.card.cardId - b.card.cardId
            })

            this.cards = []
            this.cardsMap = {}
            const allCompanies = this.allAccessGroupCompanyAssociationsForProject.filter(
                (groupCompany) => !groupCompany.company
            )
            // hide group card companies if there are is option for [all companies]
            if (allCompanies.length) {
                allCompanies.forEach(this.setGroupCompanyById)
            } else {
                this.allAccessGroupCompanyAssociationsForProject.forEach(this.setGroupCompanyById)
            }
            this.allAccessGroupCardAssociationsForProject.forEach(this.setGroupCardById)
        },
    },
    async mounted() {
        try {
            this.isFetching = true
            const projectResult = await accessClient.query({
                query: PROJECT_QUERY,
                variables: { projectId: this.currentProjectId },
                fetchPolicy: 'network-only',
            })
            this.project = projectResult.data.project
            const accessControlResult = await accessClient.query({
                query: PROJECT_ACCESS_QUERY,
                variables: {
                    projectId: this.currentProjectId,
                    withPsi: this.withPsi,
                },
                fetchPolicy: 'network-only',
            })
            this.allReadersForProject = accessControlResult.data.allReadersForProject
            this.allRulesForProject = [...accessControlResult.data.allRulesForProject]
            this.allRulesForProject.sort((a, b) => a.name.localeCompare(b.name))
            this.allGroupsForProject = [...accessControlResult.data.allGroupsForProject]
            this.allGroupReaderRuleAssociationsForProject =
                accessControlResult.data.allGroupReaderRuleAssociationsForProject
            this.allAccessGroupCompanyAssociationsForProject = [
                ...accessControlResult.data.allAccessGroupCompanyAssociationsForProject,
            ]
            this.allAccessGroupCardAssociationsForProject = [
                ...accessControlResult.data.allAccessGroupCardAssociationsForProject,
            ]
            this.sortAndSetCards()
        } finally {
            this.isFetching = false
        }
    },
}
